/**
 * The trade "legs" a user can mark on the chart. tone → colour, glyph + label show
 * on marker/composer, marker = the custom overlay name, lead seeds the composer.
 * Labels read as ANNOTATIONS of intent, not order commands — the user is describing
 * a plan ("buy entry"), not placing a trade ("buy").
 */
export type LegTone = 'success' | 'danger' | 'neutral'

export interface LegType {
  kind: string
  label: string
  tone: LegTone
  glyph: string
  mark: 'point' | 'level'
  /** Custom overlay name drawn for this leg. */
  marker: string
  /** Composer lead-in sentence. */
  lead: string
  /** Composer placeholder example. */
  ph: string
}

export const LEG_TYPES: LegType[] = [
  { kind: 'buy', label: 'Buy entry', tone: 'success', glyph: '▲', mark: 'point', marker: 'buyEntry', lead: 'I want to buy here because', ph: 'price pulled back to the trend line and bounced' },
  { kind: 'sell', label: 'Sell entry', tone: 'danger', glyph: '▼', mark: 'point', marker: 'sellEntry', lead: "I'd sell here because", ph: 'price rejected the level and rolled over' },
  { kind: 'exit', label: 'Exit', tone: 'neutral', glyph: '◆', mark: 'point', marker: 'exitPoint', lead: 'I want to exit here because', ph: 'momentum stalled near resistance' },
  { kind: 'stop', label: 'Stop', tone: 'danger', glyph: '▬', mark: 'level', marker: 'levelLine', lead: "I'd set my stop here because", ph: 'a break below here invalidates the setup' },
  { kind: 'tp', label: 'Target', tone: 'success', glyph: '▬', mark: 'level', marker: 'levelLine', lead: "I'd take profit here because", ph: 'the prior swing high is a natural target' },
]

export const legByKind = (k: string | undefined): LegType =>
  LEG_TYPES.find((l) => l.kind === k) ?? LEG_TYPES[0]

/** Overlay names that carry a leg note in extendData (hover-card hit targets). */
export const LEG_MARKERS = ['buyEntry', 'sellEntry', 'exitPoint', 'levelLine']

/**
 * Overlay names that form a guided entry→exit setup, joined to each other and
 * to their dashed connector by a shared extendData.setupId. levelLine is
 * deliberately excluded: stop/target levels are standalone, not setup members.
 */
export const SETUP_MARKERS = ['buyEntry', 'sellEntry', 'exitPoint']

/** Marker colours per direction (match the overlay drawing code). */
export const LEG_COLORS: Record<'buy' | 'sell' | 'exit', string> = {
  buy: '#089981',
  sell: '#f23645',
  exit: '#9598a1',
}
